// src/components/Account/HelpSupport/CommunityGuidelines.tsx
import React from 'react';
 
const C = {
    primary:          '#6a37d4',
    primaryContainer: '#ede8ff',
    surface:          '#f3f6ff',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
// ── Shared typography components ──────────────────────────────────────────────
const H2: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 style={{
        fontFamily: "'Epilogue', sans-serif",
        fontSize: '1.35rem', fontWeight: '900',
        color: C.onBackground,
        margin: '40px 0 12px 0',
        paddingBottom: '8px',
        borderBottom: `2px solid ${C.surfaceHigh}`,
    }}>
        {children}
    </h2>
);
 
const H3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 style={{
        fontFamily: "'Epilogue', sans-serif",
        fontSize: '1.05rem', fontWeight: '800',
        color: C.primary,
        margin: '24px 0 8px 0',
    }}>
        {children}
    </h3>
);
 
const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p style={{
        fontSize: '0.95rem', lineHeight: '1.8',
        color: C.onSurface, margin: '0 0 14px 0',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
        {children}
    </p>
);
 
const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <ul style={{
        margin: '0 0 16px 0', paddingLeft: '20px',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
        {children}
    </ul>
);
 
const LI: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li style={{
        fontSize: '0.95rem', lineHeight: '1.8',
        color: C.onSurface, marginBottom: '6px',
    }}>
        {children}
    </li>
);
 
const SubHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p style={{
        fontFamily: "'Epilogue', sans-serif",
        fontSize: '0.9rem', fontWeight: '800',
        color: C.onBackground, margin: '20px 0 8px 0',
    }}>
        {children}
    </p>
);
 
// ── Page ─────────────────────────────────────────────────────────────────────
const CommunityGuidelines: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        background: C.surface,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        padding: '48px 24px 80px',
    }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
 
            {/* Header */}
            <div style={{ marginBottom: '40px' }}>
                <div style={{
                    display: 'inline-block',
                    background: C.primaryContainer,
                    color: C.primary,
                    padding: '4px 14px', borderRadius: '9999px',
                    fontSize: '0.75rem', fontWeight: '700',
                    letterSpacing: '1px', textTransform: 'uppercase',
                    fontFamily: "'Epilogue', sans-serif",
                    marginBottom: '16px',
                }}>
                    Legal
                </div>
                <h1 style={{
                    fontFamily: "'Epilogue', sans-serif",
                    fontSize: '2.4rem', fontWeight: '900',
                    color: C.onBackground, margin: '0 0 8px 0',
                }}>
                    Community Guidelines
                </h1>
                <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0 }}>
                    Last updated: November 28, 2025
                </p>
            </div>
 
            {/* Card */}
            <div style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '48px',
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                border: `1px solid ${C.surfaceHigh}`,
            }}>
                <P>
                    These Community Guidelines establish the rules and expectations for all users of Scriblish. They work in conjunction with our Terms of Service and Privacy Policy to create a safe, positive, and educational environment for children.
                </P>
 
                {/* 1 */}
                <H2>1. Core Community Principles</H2>
 
                <H3>1.1 Safety First</H3>
                <P>Child safety is our absolute priority. The platform is designed to:</P>
                <UL>
                    <LI>Prevent sharing of personal identifying information</LI>
                    <LI>Moderate all public content before it goes live</LI>
                    <LI>Provide easy reporting mechanisms for concerning content</LI>
                    <LI>Maintain COPPA compliance at all times</LI>
                </UL>
 
                <H3>1.2 Respect and Kindness</H3>
                <P>All community members must treat others with respect. We have zero tolerance for:</P>
                <UL>
                    <LI>Bullying, harassment, or intimidation</LI>
                    <LI>Discriminatory language or behavior</LI>
                    <LI>Personal attacks or mean-spirited criticism</LI>
                    <LI>Trolling or deliberately disruptive behavior</LI>
                </UL>
 
                <H3>1.3 Age-Appropriate Content</H3>
                <P>All content must be suitable for children ages 8–12. The platform is an educational space focused on creative writing development.</P>
 
                <H3>1.4 Authenticity and Originality</H3>
                <P>Users must create original content and properly attribute any inspiration or references. Plagiarism is not permitted.</P>
 
                {/* 2 */}
                <H2>2. Content Standards</H2>
 
                <H3>2.1 Acceptable Content</H3>
                <P>Users are encouraged to write:</P>
                <UL>
                    <LI>Original fiction (fantasy, adventure, mystery, sci-fi, realistic fiction)</LI>
                    <LI>Poetry and creative non-fiction</LI>
                    <LI>Educational or informative pieces appropriate for their age group</LI>
                    <LI>Fan fiction (that doesn't violate copyright or contain inappropriate themes)</LI>
                </UL>
 
                <H3>2.2 Prohibited Content</H3>
                <P>The following content is strictly forbidden:</P>
 
                <SubHead>Personal Information:</SubHead>
                <UL>
                    <LI>Full names (first and last)</LI>
                    <LI>Physical addresses, phone numbers, email addresses</LI>
                    <LI>School names, locations, or identifying information</LI>
                    <LI>Social media handles or links to external profiles</LI>
                    <LI>Photos or videos of real people (children or adults)</LI>
                </UL>
 
                <SubHead>Inappropriate or Harmful Content:</SubHead>
                <UL>
                    <LI>Violence, gore, or graphic descriptions of harm</LI>
                    <LI>Sexual content or romantic content inappropriate for children</LI>
                    <LI>Drug, alcohol, or substance abuse content</LI>
                    <LI>Profanity, vulgar language, or crude humor</LI>
                    <LI>Self-harm, suicide, or eating disorder content</LI>
                    <LI>Instructions for dangerous or illegal activities</LI>
                    <LI>Hate speech, slurs, or discriminatory content</LI>
                </UL>
 
                <SubHead>Intellectual Property Violations:</SubHead>
                <UL>
                    <LI>Plagiarized content from books, websites, or other sources</LI>
                    <LI>Copyrighted material used without permission</LI>
                    <LI>Impersonation of authors, characters, or public figures</LI>
                </UL>
 
                <SubHead>Platform Abuse:</SubHead>
                <UL>
                    <LI>Spam, repetitive content, or advertisements</LI>
                    <LI>Links to external websites (except with explicit permission)</LI>
                    <LI>Attempts to circumvent safety features</LI>
                    <LI>Fake accounts or impersonation</LI>
                </UL>
 
                {/* 3 */}
                <H2>3. Interaction Guidelines</H2>
 
                <H3>3.1 Comments and Feedback</H3>
                <P>Constructive feedback should:</P>
                <UL>
                    <LI>Be specific and helpful</LI>
                    <LI>Focus on the writing, not the writer</LI>
                    <LI>Offer suggestions, not just criticism</LI>
                    <LI>Use encouraging and supportive language</LI>
                </UL>
 
                <P>Prohibited comments include:</P>
                <UL>
                    <LI>Personal attacks or insults</LI>
                    <LI>Dismissive or discouraging remarks</LI>
                    <LI>Off-topic or spam comments</LI>
                    <LI>Requests for personal information</LI>
                </UL>
 
                {/* 4 */}
                <H2>4. Moderation and Enforcement</H2>
 
                <H3>4.1 Content Moderation Process</H3>
                <SubHead>Automated Screening:</SubHead>
                <UL>
                    <LI>AI-powered filters scan all content for prohibited material</LI>
                    <LI>Automatic blocking of personal information (addresses, phone numbers, etc.)</LI>
                    <LI>Flagging of potentially inappropriate language or themes</LI>
                </UL>
 
                <SubHead>Human Review:</SubHead>
                <UL>
                    <LI>Trained moderators review flagged content within 24 hours</LI>
                    <LI>All public stories undergo review before publication</LI>
                    <LI>User-reported content receives priority review</LI>
                </UL>
 
                <H3>4.2 Reporting Mechanism</H3>
                <P>Users can report content or behavior through the report button on any story or comment. All reports are reviewed confidentially. False or malicious reporting may result in consequences.</P>
 
                <H3>4.3 Enforcement Actions</H3>
                <P>Violations result in graduated responses:</P>
 
                {[
                    {
                        level: 'Level 1 — First Offense (Minor)',
                        items: [
                            'Educational notification to user and parent',
                            'Content removal if necessary',
                            'No account restrictions',
                        ],
                    },
                    {
                        level: 'Level 2 — Warning',
                        items: [
                            'Formal warning issued',
                            'Parent notification required',
                            'Temporary restriction (24–48 hours) of certain features',
                            'Required acknowledgment of guidelines',
                        ],
                    },
                    {
                        level: 'Level 3 — Suspension',
                        items: [
                            'Account suspension (3–30 days depending on severity)',
                            'Immediate parent notification and consultation',
                            'Review meeting required before reinstatement',
                            'Probationary period upon return',
                        ],
                    },
                    {
                        level: 'Level 4 — Termination',
                        items: [
                            'Permanent account removal for severe or repeated violations',
                            'Applies to: safety threats, severe bullying, sharing explicit content, repeated violations after suspension',
                            'IP/device restrictions may be applied',
                            'No refunds for paid subscriptions',
                        ],
                    },
                ].map(({ level, items }) => (
                    <div key={level} style={{
                        background: C.surface, borderRadius: '12px',
                        padding: '16px 20px', marginBottom: '12px',
                        border: `1px solid ${C.surfaceHigh}`,
                    }}>
                        <SubHead>{level}</SubHead>
                        <UL>{items.map(i => <LI key={i}>{i}</LI>)}</UL>
                    </div>
                ))}
 
                {/* 5 */}
                <H2>5. Parental Responsibilities</H2>
                <P>Parents and guardians are expected to:</P>
                <UL>
                    <LI>Review these guidelines with their child</LI>
                    <LI>Monitor their child's activity regularly</LI>
                    <LI>Discuss appropriate online behavior and digital citizenship</LI>
                    <LI>Maintain current contact information</LI>
                    <LI>Respond to platform notifications about their child's account</LI>
                </UL>
 
                {/* 6 */}
                <H2>6. Contact and Support</H2>
                <P>
                    For questions, concerns, or to report violations, please contact us at{' '}
                    <a href="mailto:scriblishofficial@gmail.com" style={{ color: C.primary, fontWeight: '700', textDecoration: 'none' }}>
                        scriblishofficial@gmail.com
                    </a>
                </P>
                <P>If you believe a child is in immediate danger, contact local law enforcement immediately (911 in the US) and then notify us.</P>
 
                {/* 7 */}
                <H2>7. Changes to Guidelines</H2>
                <P>We may update these Community Guidelines from time to time. Continued use of Scriblish after changes are posted constitutes acceptance of the updated guidelines.</P>
 
                {/* Footer links */}
                <div style={{
                    marginTop: '48px', paddingTop: '28px',
                    borderTop: `1px solid ${C.surfaceHigh}`,
                    display: 'flex', gap: '20px', flexWrap: 'wrap',
                    justifyContent: 'center',
                }}>
                    {[
                        { label: 'About',           href: '/about' },
                        { label: 'Privacy Policy',  href: '/privacy-policy' },
                        { label: 'Terms of Service',href: '/terms-of-service' },
                        { label: 'Contact Us',      href: '/contact' },
                    ].map(({ label, href }) => (
                        <a key={label} href={href} style={{
                            color: C.primary, textDecoration: 'none',
                            fontSize: '0.85rem', fontWeight: '600',
                            fontFamily: "'Epilogue', sans-serif",
                            transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.7'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                        >
                            {label}
                        </a>
                    ))}
                </div>
            </div>
        </div>
    </div>
);
 
export default CommunityGuidelines;
